import { X } from 'lucide-react'
import { toast } from 'sonner'

import { useEffect, useState } from 'react'

import { stringifyGraph } from '@/clients/http'
import { useAuth } from '@/contexts/AuthContext'
import { type Branch, BranchModel } from '@/http/models/Branch'
import { UserModel } from '@/http/models/User'
import { apiErrorMessage } from '@/lib/apiError'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'

interface Props {
  branch: Branch | null
  onClose: () => void
  onChanged: () => void
}

type StaffOption = { _id: string; name: string; email?: string; phone?: string }

/** Who works at a branch (`branches:add_staff` / `remove_staff`). The till limits password sign-in to these staff. */
export const BranchStaffDialog = ({ branch, onClose, onChanged }: Props) => {
  const { can } = useAuth()
  const [staff, setStaff] = useState<StaffOption[]>([])
  const [allStaff, setAllStaff] = useState<StaffOption[]>([])
  const [picked, setPicked] = useState('')
  const [busy, setBusy] = useState<string | null>(null)

  useEffect(() => {
    setStaff(branch?.staffs ?? [])
    setPicked('')
    if (!branch || !can('branches:add_staff')) return
    UserModel.getList({ query: { length: 200, graph_enc: stringifyGraph({ _id: true, name: true, email: true, phone: true }) } }).then((res) => {
      if (res.success) setAllStaff(((res.data as { result?: StaffOption[] } | null)?.result) ?? [])
    })
  }, [branch, can])

  if (!branch) return null

  const add = async () => {
    if (!picked) return
    setBusy(picked)
    const res = await BranchModel.addStaff(branch._id, picked)
    setBusy(null)
    if (!res.success) {
      toast.error(apiErrorMessage(res, 'Could not add the staff member'))
      return
    }
    const member = allStaff.find((s) => s._id === picked)
    if (member) setStaff((prev) => [...prev, member])
    setPicked('')
    onChanged()
  }

  const remove = async (id: string) => {
    setBusy(id)
    const res = await BranchModel.removeStaff(branch._id, id)
    setBusy(null)
    if (!res.success) {
      toast.error(apiErrorMessage(res, 'Could not remove the staff member'))
      return
    }
    setStaff((prev) => prev.filter((s) => s._id !== id))
    onChanged()
  }

  const available = allStaff.filter((s) => !staff.some((m) => m._id === s._id))

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Staff at {branch.name}</DialogTitle>
          <DialogDescription>Staff listed here can sign in with a password on this branch's POS devices.</DialogDescription>
        </DialogHeader>

        {can('branches:add_staff') && (
          <div className="flex gap-2">
            <Select value={picked} onValueChange={setPicked}>
              <SelectTrigger aria-label="Staff member">
                <SelectValue placeholder="Choose a staff member" />
              </SelectTrigger>
              <SelectContent>
                {available.map((s) => (
                  <SelectItem key={s._id} value={s._id}>{s.name}{s.email ? ` (${s.email})` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={add} disabled={!picked || !!busy}>
              {busy === picked && picked && <Spinner />}
              Add
            </Button>
          </div>
        )}

        {staff.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No staff assigned yet.</p>
        ) : (
          <ul className="divide-y divide-border-subtle rounded-md border border-border-subtle">
            {staff.map((s) => (
              <li key={s._id} className="flex items-center justify-between gap-3 px-3 py-2">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-ink">{s.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{s.email || s.phone || ''}</div>
                </div>
                {can('branches:remove_staff') && (
                  <Button variant="ghost" size="icon" aria-label={`Remove ${s.name}`} onClick={() => remove(s._id)} disabled={!!busy}>
                    {busy === s._id ? <Spinner /> : <X />}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  )
}
